/* UGC carousel - drag, momentum, seamless loop

   The first half of the loop is real editor-managed content rendered by
   blocks/yg-ugc-item.liquid; only real DOM nodes carry block.shopify_attributes,
   so this clones that set once for the second half and strips those attributes.
   State lives in an attach(root) closure so several carousels can coexist, and
   is destroyed on shopify:section:unload or a theme-editor edit leaves its
   animation and observer running on detached nodes. The hover pause is gated on (hover: hover):
   on a touchscreen the emulated mouseenter after a tap has no matching
   mouseleave, so ungated it stalls the drift indefinitely. */

(function () {
  'use strict';

  var ROOT = '[data-ugc-carousel]';
  var HOVER = window.matchMedia('(hover: hover)');
  var DRIFT_PX_PER_S = 33;
  var INTENT_THRESHOLD = 10;
  var DRAG_RELEASE_PAUSE_MS = 1400;
  var NUDGE_PAUSE_MS = 1600;
  var NUDGE_DURATION_MS = 480;
  var VELOCITY_DECAY = 0.94;
  var VELOCITY_FLOOR = 0.02;

  /** sectionId -> controller with a destroy() method */
  var instances = Object.create(null);

  /**
   * Strips editor-only attributes from a cloned subtree so the clone is never
   * picked up as a second, ambiguous copy of the same block.
   *
   * @param {Element} root
   */
  function stripEditorAttrs(root) {
    var els = root.querySelectorAll('[data-shopify-editor-block-id], [data-block-id]');
    for (var i = 0; i < els.length; i++) {
      var attrs = Array.prototype.slice.call(els[i].attributes);
      for (var j = 0; j < attrs.length; j++) {
        var name = attrs[j].name;
        if (name.indexOf('data-shopify') === 0 || name === 'data-block-id') {
          els[i].removeAttribute(name);
        }
      }
    }
  }

  /**
   * Builds the loop's second half by cloning the server-rendered first half,
   * once per fresh render. Each original is stamped with its position first so
   * the clone inherits it - yg-ugc-modal.js opens by index. Clones are hidden
   * from assistive tech, and their photo triggers need tabindex="-1" or every
   * entry would be two tab stops.
   *
   * @param {Element} track
   */
  function ensureClonedHalf(track) {
    if (track.dataset.ugcCloned === 'true') return;

    var originals = Array.prototype.slice.call(track.children);
    for (var i = 0; i < originals.length; i++) {
      originals[i].dataset.ugcIndex = String(i);
    }

    for (var j = 0; j < originals.length; j++) {
      var clone = originals[j].cloneNode(true);
      stripEditorAttrs(clone);
      clone.dataset.ugcClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      var focusables = clone.querySelectorAll('[tabindex="0"]');
      for (var k = 0; k < focusables.length; k++) {
        focusables[k].setAttribute('tabindex', '-1');
      }
      track.appendChild(clone);
    }

    track.dataset.ugcCloned = 'true';
  }

  /**
   * @param {Element} root
   * @returns {{destroy: function(): void}|null}
   */
  function attach(root) {
    var viewport = root.querySelector('[data-ugc-viewport]');
    var track = root.querySelector('[data-ugc-track]');
    var prevBtn = root.querySelector('[data-ugc-prev]');
    var nextBtn = root.querySelector('[data-ugc-next]');
    if (!viewport || !track || !track.children.length || typeof track.animate !== 'function') return null;

    ensureClonedHalf(track);

    var destroyed = false;
    var drift = null;
    var visible = false;

    var cardStep = 0;
    var half = 0;
    var dragging = false;
    var startX = 0;
    var startY = 0;
    var startPos = 0;
    var velocity = 0;
    var lastX = 0;
    var lastT = 0;
    var idleTimer = null;
    var paused = false;
    var momentumRaf = null;
    var nudgeRaf = null;
    /* 'pending' | 'horizontal' | 'vertical', decided once a touch clears
       INTENT_THRESHOLD in either axis - lets touch-action: pan-y leave a
       mostly-vertical touch to native page scroll. */
    var intent = 'pending';

    function duration() {
      return (half / DRIFT_PX_PER_S) * 1000;
    }

    function wrap(pos) {
      pos = pos % half;
      return pos > 0 ? pos - half : pos;
    }

    function getPos() {
      if (!drift || !half) return 0;
      var d = duration();
      var t = Number(drift.currentTime) || 0;
      return -(((t % d) + d) % d) / d * half;
    }

    function setPos(pos) {
      if (!drift || !half) return 0;
      pos = wrap(pos);
      drift.currentTime = (-pos / half) * duration();
      return pos;
    }

    function update() {
      if (!drift) return;
      var run = visible && !dragging && !paused && !momentumRaf && !nudgeRaf;
      if (run && drift.playState !== 'running') drift.play();
      else if (!run && drift.playState === 'running') drift.pause();
    }

    function measure() {
      var first = track.children[0];
      var second = track.children[1];
      if (first && second) cardStep = second.offsetLeft - first.offsetLeft;
    }

    function recalc() {
      measure();
      var next = track.scrollWidth / 2;
      if (!next || (drift && Math.abs(next - half) < 0.5)) return;

      var pos = getPos();
      if (drift) drift.cancel();
      half = next;
      drift = track.animate(
        [{ transform: 'translateX(0px)' }, { transform: 'translateX(' + -half + 'px)' }],
        { duration: duration(), iterations: Infinity, easing: 'linear' }
      );
      drift.pause();
      setPos(pos);
      update();
    }

    function pauseFor(ms) {
      paused = true;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () {
        paused = false;
        update();
      }, ms);
      update();
    }

    function stopMomentum() {
      if (!momentumRaf) return;
      cancelAnimationFrame(momentumRaf);
      momentumRaf = null;
    }

    function startMomentum() {
      if (Math.abs(velocity) <= VELOCITY_FLOOR) return;
      var pos = getPos();

      function step() {
        if (destroyed) return;
        pos = setPos(pos + velocity);
        velocity *= VELOCITY_DECAY;
        if (visible && Math.abs(velocity) > VELOCITY_FLOOR) {
          momentumRaf = requestAnimationFrame(step);
        } else {
          momentumRaf = null;
          update();
        }
      }

      momentumRaf = requestAnimationFrame(step);
      update();
    }

    function onPointerDown(event) {
      /* setPointerCapture() retargets every subsequent event for this pointer,
         including the browser's real click synthesis, to viewport rather than
         where the finger is - so a plain tap on the "Read more" link resolves
         its default action against the wrong element and never navigates.
         Scripted clicks cannot reproduce it: dispatchEvent()/.click() always
         fire normal click semantics regardless of capture state. Fix is to
         never start a drag from an interactive descendant at all. */
      if (event.target.closest && event.target.closest('a, button')) return;

      stopMomentum();
      dragging = true;
      update();
      intent = 'pending';
      velocity = 0;
      startX = event.clientX;
      startY = event.clientY;
      startPos = getPos();
      lastX = event.clientX;
      lastT = performance.now();
      viewport.classList.add('dragging');
      viewport.setPointerCapture(event.pointerId);
      clearTimeout(idleTimer);
      paused = true;
      /* no preventDefault: touch-action: pan-y must stay free to start a
         native vertical scroll if that is where this touch is headed */
    }

    /**
     * Resolves 'pending' intent once movement clears INTENT_THRESHOLD, handing
     * a vertical gesture off to native scroll - releasing capture and resetting
     * drag state as a pointerup would, without waiting for one.
     *
     * @param {number} dx
     * @param {number} dy
     * @param {PointerEvent} event
     * @returns {boolean} true once intent is 'horizontal' and the caller
     *   should keep going; false if the move has nothing left to do.
     */
    function resolveIntent(dx, dy, event) {
      if (intent !== 'pending') return intent === 'horizontal';

      if (Math.abs(dx) < INTENT_THRESHOLD && Math.abs(dy) < INTENT_THRESHOLD) return false;

      intent = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      if (intent === 'horizontal') return true;

      dragging = false;
      viewport.classList.remove('dragging');
      viewport.releasePointerCapture(event.pointerId);
      clearTimeout(idleTimer);
      paused = false;
      update();
      return false;
    }

    function onPointerMove(event) {
      if (!dragging) return;
      var dx = event.clientX - startX;
      var dy = event.clientY - startY;
      if (!resolveIntent(dx, dy, event)) return;

      /* only reached once intent is 'horizontal' - keeps vertical scroll
         locked out for the rest of THIS drag, which pan-y alone would not */
      event.preventDefault();
      setPos(startPos + dx);
      var now = performance.now();
      var dt = now - lastT || 16;
      velocity = ((event.clientX - lastX) / dt) * 16;
      lastX = event.clientX;
      lastT = now;
    }

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('dragging');
      pauseFor(DRAG_RELEASE_PAUSE_MS);
      startMomentum();
    }

    function onMouseEnter() {
      if (!HOVER.matches || dragging) return;
      paused = true;
      update();
    }

    function onMouseLeave() {
      if (!HOVER.matches || dragging) return;
      clearTimeout(idleTimer);
      paused = false;
      update();
    }

    function nudge(dir) {
      clearTimeout(idleTimer);
      paused = true;
      stopMomentum();
      if (nudgeRaf) cancelAnimationFrame(nudgeRaf);
      nudgeRaf = null;
      update();

      var startVal = getPos();
      var target = startVal + dir * -cardStep;
      var t0 = performance.now();

      function step(now) {
        if (destroyed) return;
        var t = Math.min(1, (now - t0) / NUDGE_DURATION_MS);
        var eased = 1 - Math.pow(1 - t, 3);
        setPos(startVal + (target - startVal) * eased);
        if (t < 1) {
          nudgeRaf = requestAnimationFrame(step);
        } else {
          nudgeRaf = null;
          pauseFor(NUDGE_PAUSE_MS);
        }
      }
      nudgeRaf = requestAnimationFrame(step);
    }

    function onPrevClick() {
      nudge(-1);
    }

    function onNextClick() {
      nudge(1);
    }

    function onResize() {
      recalc();
    }

    var observer = new IntersectionObserver(function (entries) {
      visible = entries[entries.length - 1].isIntersecting;
      if (!visible) stopMomentum();
      update();
    });

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointerleave', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('mouseenter', onMouseEnter);
    viewport.addEventListener('mouseleave', onMouseLeave);
    if (prevBtn) prevBtn.addEventListener('click', onPrevClick);
    if (nextBtn) nextBtn.addEventListener('click', onNextClick);
    window.addEventListener('resize', onResize);

    recalc();
    var recalcTimer = setTimeout(recalc, 200);
    observer.observe(viewport);

    return {
      destroy: function () {
        destroyed = true;
        observer.disconnect();
        if (drift) drift.cancel();
        stopMomentum();
        if (nudgeRaf) cancelAnimationFrame(nudgeRaf);
        clearTimeout(idleTimer);
        clearTimeout(recalcTimer);
        viewport.removeEventListener('pointerdown', onPointerDown);
        viewport.removeEventListener('pointermove', onPointerMove);
        viewport.removeEventListener('pointerup', endDrag);
        viewport.removeEventListener('pointerleave', endDrag);
        viewport.removeEventListener('pointercancel', endDrag);
        viewport.removeEventListener('mouseenter', onMouseEnter);
        viewport.removeEventListener('mouseleave', onMouseLeave);
        if (prevBtn) prevBtn.removeEventListener('click', onPrevClick);
        if (nextBtn) nextBtn.removeEventListener('click', onNextClick);
        window.removeEventListener('resize', onResize);
      },
    };
  }

  function init() {
    var roots = document.querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var sectionId = root.dataset.sectionId;
      if (!sectionId || instances[sectionId]) continue;

      var controller = attach(root);
      if (controller) instances[sectionId] = controller;
    }
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function (event) {
    var sectionId = event.detail && event.detail.sectionId;
    if (!sectionId || !instances[sectionId]) return;
    instances[sectionId].destroy();
    delete instances[sectionId];
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
